import React, { useEffect } from 'react';
import { X, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

/**
 * Focused modal that shows a single metric's history — big chart + breakdown table.
 *
 * Props:
 *   isOpen, onClose, icon, label, currentValue, series (array of { bucket, value }),
 *   bucketLabel (fn), formatValue (fn), granularity
 */
export const MetricDetailModal = ({
  isOpen,
  onClose,
  icon: Icon,
  label,
  currentValue,
  series = [],
  bucketLabel = (k) => k,
  formatValue = (v) => v,
  granularity = 'month',
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const chartData = series.map((p) => ({ bucket: bucketLabel(p.bucket), value: p.value || 0, rawBucket: p.bucket }));
  const lastVal = series.length > 0 ? series[series.length - 1].value || 0 : 0;
  const prevVal = series.length > 1 ? series[series.length - 2].value || 0 : 0;
  const prevBucket = series.length > 1 ? series[series.length - 2].bucket : null;
  let deltaPct = null;
  let deltaDir = 'flat';
  if (prevVal > 0) {
    deltaPct = Math.round(((lastVal - prevVal) / prevVal) * 100);
    deltaDir = deltaPct > 0 ? 'up' : (deltaPct < 0 ? 'down' : 'flat');
  } else if (lastVal > 0) {
    deltaDir = 'up';
  }
  const DeltaIcon = deltaDir === 'up' ? ArrowUpRight : deltaDir === 'down' ? ArrowDownRight : Minus;
  const deltaCls = deltaDir === 'up' ? 'text-emerald-700' : deltaDir === 'down' ? 'text-red-600' : 'text-gray-400';

  const total = series.reduce((s, p) => s + (p.value || 0), 0);
  const best = series.reduce((m, p) => (p.value || 0) > (m?.value || 0) ? p : m, null);
  const avg = series.length > 0 ? total / series.length : 0;

  const periodWord =
    granularity === 'month' ? 'month'
    : granularity === 'quarter' ? 'quarter'
    : granularity === 'week' ? 'week'
    : granularity === 'day' ? 'day'
    : 'year';
  const periodPlural =
    granularity === 'month' ? 'months'
    : granularity === 'quarter' ? 'quarters'
    : granularity === 'week' ? 'weeks'
    : granularity === 'day' ? 'days'
    : 'years';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="metric-detail-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex-shrink-0">
              <Icon className="w-5 h-5 text-blue-600" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 tracking-tight truncate">{label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">History across the last {series.length} {periodPlural}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            aria-label="Close"
            data-testid="metric-detail-close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Big number + delta */}
          <div>
            <div className="flex items-baseline gap-3">
              <div className="text-[42px] font-semibold text-gray-900 tracking-tight leading-none tabular-nums" data-testid="metric-detail-value">
                {currentValue}
              </div>
              {deltaPct !== null && (
                <span className={`inline-flex items-center gap-0.5 text-sm font-semibold tabular-nums ${deltaCls}`}>
                  <DeltaIcon className="w-4 h-4" />
                  {Math.abs(deltaPct)}%
                </span>
              )}
              {deltaPct !== null && prevBucket && (
                <span className="text-sm text-gray-500">vs {bucketLabel(prevBucket)}</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">Current {periodWord}</p>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-4 border-y border-gray-100 py-4">
            <SummaryStat label={`Total (last ${series.length} ${periodPlural})`} value={formatValue(total)} />
            <SummaryStat label={`Average per ${periodWord}`} value={formatValue(Math.round(avg))} />
            <SummaryStat label="Best period" value={best ? formatValue(best.value || 0) : '—'} sub={best ? bucketLabel(best.bucket) : null} />
          </div>

          {/* Chart */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              {granularity === 'month' ? 'Month-by-month'
                : granularity === 'quarter' ? 'Quarter-by-quarter'
                : granularity === 'week' ? 'Week-by-week'
                : granularity === 'day' ? 'Day-by-day'
                : 'Year-by-year'} trend
            </h4>
            <div className="h-64" data-testid="metric-detail-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(v) => [formatValue(v), label]}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Breakdown table */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Breakdown</h4>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="metric-detail-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">vs prev</th>
                  </tr>
                </thead>
                <tbody>
                  {series.map((p, idx) => {
                    const prev = idx > 0 ? series[idx - 1].value || 0 : null;
                    const cur = p.value || 0;
                    let rowDelta = null;
                    if (prev !== null && prev > 0) rowDelta = Math.round(((cur - prev) / prev) * 100);
                    return (
                      <tr key={p.bucket} className="border-t border-gray-100 first:border-t-0">
                        <td className="px-4 py-2.5 text-gray-700">{bucketLabel(p.bucket)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-900 font-medium tabular-nums">{formatValue(cur)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {rowDelta === null ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <span className={rowDelta > 0 ? 'text-emerald-700' : rowDelta < 0 ? 'text-red-600' : 'text-gray-400'}>
                              {rowDelta > 0 ? '+' : ''}{rowDelta}%
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, sub }) => (
  <div>
    <div className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">{label}</div>
    <div className="text-lg font-semibold text-gray-900 tabular-nums mt-1 leading-none">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);
